'use client';

import * as React from 'react';
import { ChevronsDownUp, ChevronsUpDown } from 'lucide-react';

import { cn } from '@/lib/utils';
import { useSearchHighlightText } from '../../hook/useSearchHighlightText';
import SearchWithNavigation from '../common/search-with-navigation';

export interface ExpandableTextBoxProps {
  label: string;
  content: string;
  defaultOpen?: boolean;
  className?: string;
}

export const ExpandableTextBox: React.FC<ExpandableTextBoxProps> = ({
  label,
  content,
  defaultOpen = false,
  className,
}) => {
  const [isOpen, setIsOpen] = React.useState(defaultOpen);

  const lines = content.split('\n');

  const {
    term,
    setTerm,
    handleClearSearch,
    prevMatch,
    nextMatch,
    matches,
    currentIndex,
    beginHighlightPass,
    getHighlightedNodes,
  } = useSearchHighlightText(lines);

  if (content === '') return;

  const handleClose = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleClearSearch();
    setIsOpen(false);
  };

  beginHighlightPass();

  return (
    <div className={cn('overflow-hidden rounded bg-[#0000007A]', className)}>
      {/* Header */}
      <div
        className={cn(
          'flex select-none items-center justify-between border-transparent px-3 border pt-[11px] pb-[3px]',
          isOpen && 'border border-b border-[#0A343C]'
        )}
        onClick={() => {
          if (!isOpen) setIsOpen(true);
        }}
      >
        {/* Label */}
        <span className="text-xs text-form-input-disabled">{label}</span>

        {isOpen ? (
          /* Search + close when expanded */
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <SearchWithNavigation
              value={term}
              onChange={setTerm}
              onClear={handleClearSearch}
              placeholder="Search keyword"
              matches={matches}
              currentIndex={currentIndex}
              onPreviousMatch={prevMatch}
              onNextMatch={nextMatch}
              disabled={false}
            />
            <button
              onClick={handleClose}
              className="text-[#8C9EA1]"
            >
              <ChevronsDownUp size={16} />
            </button>
          </div>
        ) : (
          /* Expand icon when collapsed */
          <button
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(true);
            }}
            className="text-[#8C9EA1] transition-colors hover:text-foreground"
          >
            <ChevronsUpDown size={16} />
          </button>
        )}
      </div>

      {/* Body */}
      {isOpen ? (
        /* Expanded: full content with line numbers (flex version) */
        <div className="max-h-[468px] overflow-auto text-xs">
          {lines.map((text, i) => (
            <div key={i} className="flex hover:bg-white/5">
              {/* Line number gutter */}
              <div className="w-10 shrink-0 select-none border-r  border-line-tertiary bg-[#FFFFFF05] px-2 text-right text-[#40565A]">
                {i + 1}
              </div>

              {/* Line content */}
              <div className="flex-1 whitespace-pre-wrap break-all px-3 text-body-subtle">
                {getHighlightedNodes(text || ' ')}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Collapsed: preview */
        <div className="px-3 pb-4 pt-1">
          <p className="line-clamp-2 whitespace-pre-wrap text-xs text-body-subtle">
            {content}
          </p>
        </div>
      )}
    </div>
  );
};

export default ExpandableTextBox;
